/**
 * Taking your preferences with you.
 *
 * Setting the app up the way you like it is twenty minutes of work, and on a
 * new machine it was twenty minutes again. This writes the safe half of your
 * settings to a file and reads it back.
 *
 * Deliberately only the safe half. No token, no password, no conversations,
 * no friends, nothing that identifies you: a file somebody might drop in a
 * chat by accident should be worth nothing to whoever picks it up. What goes
 * in is a fixed list - appearance, chat, sound, keybinds - so a future
 * setting is left out until somebody decides it belongs.
 */

import { useRef, useState } from 'react';
import type { Settings } from '@/lib/types';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Button, Card, CardHead, useToast } from './ui';

/** Exactly what travels. Anything not named here is not in the file. */
const SAFE_KEYS = [
  'theme', 'accent', 'contrast', 'cornerRadius', 'shadowDepth', 'avatarShape', 'fontFamily',
  'messageSpacing', 'emojiScale', 'chatAvatars', 'colorNames', 'illustration', 'highContrast',
  'density', 'uiScale', 'appLanguage', 'chatFontSize', 'compactMessages', 'chatBubbles',
  'showTimestamps', 'clock24', 'enterToSend', 'previewLinks', 'showEmbeds', 'spellcheck',
  'typingIndicators', 'gifAutoplay', 'reduceMotion', 'dataSaver', 'sounds', 'soundVolume',
  'notifications', 'notifyPreset', 'notify', 'quiet', 'favoriteExceptions', 'keybinds',
  'pushToTalk', 'voiceNoise', 'voiceEcho', 'voiceAgc', 'screenQuality', 'autoJoinVoice',
  'confirmLeaveVoice', 'sortParties', 'sortPlayers', 'sortFriends', 'hideOfflineFriends'
] as const;

const FILE_KIND = 'pugo.preferences';

export function PrefsBackup() {
  const settings = useSession((s) => s.settings);
  const saveSettings = useSession((s) => s.saveSettings);
  const toast = useToast();
  const file = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  function save() {
    if (!settings) return;
    const picked: Record<string, unknown> = {};
    for (const key of SAFE_KEYS) {
      const value = (settings as unknown as Record<string, unknown>)[key];
      if (value !== undefined) picked[key] = value;
    }
    const blob = new Blob(
      [JSON.stringify({ kind: FILE_KIND, version: 1, savedAt: new Date().toISOString(), settings: picked }, null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pugo-preferences.json';
    a.click();
    /* The object URL is a live handle to memory; let it go once the click
       has been dealt with. */
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast('Preferences saved to a file', 'good');
  }

  async function load(f: File) {
    setBusy(true);
    try {
      const text = await f.text();
      const data = JSON.parse(text) as { kind?: string; settings?: Record<string, unknown> };
      if (data.kind !== FILE_KIND || !data.settings) throw new Error('That is not a Pugo preferences file.');

      /* Read the file, then take only the keys this version knows about -
         the file is untrusted input, not a settings object. */
      const patch: Record<string, unknown> = {};
      for (const key of SAFE_KEYS) {
        if (data.settings[key] !== undefined) patch[key] = data.settings[key];
      }
      if (!Object.keys(patch).length) throw new Error('There were no preferences in that file.');

      await saveSettings(patch as Partial<Settings>);
      toast(Object.keys(patch).length + ' preferences restored', 'good');
    } catch (err) {
      toast((err as Error).message || 'That file could not be read.', 'bad');
    } finally {
      setBusy(false);
      if (file.current) file.current.value = '';
    }
  }

  return (
    <Card>
      <CardHead title="Your preferences, as a file" />
      <p className="m-0 mb-3 text-[14px] text-mute">
        Appearance, chat, sound, voice and keybinds. Never your password, your token, your messages or your friends.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button kind="outline" icon={<Icon name="download" size={16} />} onClick={save}>Save to a file</Button>
        <Button kind="outline" icon={<Icon name="upload" size={16} />} disabled={busy} onClick={() => file.current?.click()}>
          {busy ? 'Reading…' : 'Restore from a file'}
        </Button>
        <input
          ref={file}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void load(f); }}
        />
      </div>
    </Card>
  );
}
